"use client";

import { useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { getResources, type ResourceItem } from "@/lib/resources";
import { Link } from "react-router-dom";

export default function MaterialsPage() {
  const [items, setItems] = useState<ResourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await getResources("study_materials", true);
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items
      .filter(i => i.isVisible !== false)
      .filter((i) => (i.title || "").toLowerCase().includes(s) || (i.description || "").toLowerCase().includes(s));
  }, [items, q]);

  return (
    <div className="min-h-screen bg-bg-secondary/30">
      <section className="bg-white border-b border-border pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-black text-text-primary">Study Materials</h1>
          <p className="text-text-secondary mt-2">Notes, PDFs and curated resources uploaded from the admin dashboard.</p>
          <div className="mt-6 max-w-xl relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full h-12 pl-11 pr-4 rounded-xl border border-border bg-background focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-smooth"
              placeholder="Search materials..."
            />
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="animate-spin text-primary" size={36} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-10 text-center text-text-secondary">
            No study materials found.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item) => (
              <div key={item.id} className="bg-white border border-border rounded-2xl p-6 hover:shadow-lg transition-smooth">
                <div className="text-[10px] font-black uppercase tracking-widest text-text-muted">MATERIAL</div>
                <h3 className="mt-2 text-xl font-black text-text-primary line-clamp-2">{item.title}</h3>
                <p className="mt-2 text-sm text-text-secondary line-clamp-3">{item.description}</p>
                <div className="mt-3">
                  <Link to={`/materials/${item.slug}`} className="text-sm font-black text-primary hover:underline">
                    View details
                  </Link>
                </div>
                <div className="mt-5 flex gap-2">
                  {item.documentUrls?.[0] ? (
                    <a
                      className="px-4 py-2 rounded-lg bg-primary text-white font-bold hover:bg-primary-dark transition-smooth"
                      href={item.documentUrls[0]}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download
                    </a>
                  ) : null}
                  {item.link ? (
                    <a className="px-4 py-2 rounded-lg border border-border font-bold hover:bg-bg-secondary transition-smooth" href={item.link} target="_blank" rel="noreferrer">
                      Open Link
                    </a>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

